import React, { useState, useRef } from 'react';
import Button from './ui/button';

interface MoodSliderProps {
  onContinue: (moodValue: number) => void;
  disabled?: boolean;
  className?: string;
}

const MOOD_LABELS = {
  1: 'Struggling',
  2: 'Somewhat Bad', 
  3: 'Doing Ok',
  4: 'Pretty Good',
  5: 'Awesome'
} as const;

const MOOD_EMOJIS = {
  1: '😔',
  2: '😕', 
  3: '😐',
  4: '🙂',
  5: '😊'
} as const;

const MoodSlider: React.FC<MoodSliderProps> = ({ 
  onContinue, 
  disabled = false,
  className = '' 
}) => {
  const [moodValue, setMoodValue] = useState(4); // Default to "Good"
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const sliderRef = useRef<HTMLInputElement>(null);
  const lastVibrateRef = useRef<number>(0);

  // Trigger haptic feedback on mobile
  const triggerHapticFeedback = () => {
    const now = Date.now();
    // Throttle vibrations to prevent excessive buzzing
    if (now - lastVibrateRef.current > 50) {
      if ('vibrate' in navigator) {
        navigator.vibrate(10); // Very short, subtle vibration
      }
      lastVibrateRef.current = now;
    }
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Math.round(Number(e.target.value));
    
    if (newValue !== moodValue) {
      setIsAnimating(true);
      setMoodValue(newValue);
      setHasInteracted(true);
      triggerHapticFeedback();
      
      // Reset animation after brief delay
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleSliderInteraction = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
  };

  const handleContinue = () => {
    if (hasInteracted && !disabled) {
      onContinue(moodValue);
    }
  };

  // Handle keyboard accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleContinue();
    }
  };

  return (
    <div className={`w-full max-w-md mx-auto space-y-8 ${className}`}>
      {/* Current Mood Label */}
      <div className="text-center">
        <div 
          className={`text-6xl -mb-2 transition-all duration-300 ${
            isAnimating ? 'scale-110 transform' : ''
          }`}
        >
          {MOOD_EMOJIS[moodValue as keyof typeof MOOD_EMOJIS]}
        </div>
        <div 
          className={`text-2xl font-normal mb-2 transition-all duration-300 ${
            isAnimating ? 'scale-110 transform' : ''
          }`}
          style={{ 
            color: '#111111',
            fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          }}
        >
          {MOOD_LABELS[moodValue as keyof typeof MOOD_LABELS]}
        </div>
      </div>

      {/* Slider Container */}
      <div className="space-y-6">
        {/* Slider Track */}
        <div className="relative px-4">
          <input
            ref={sliderRef}
            type="range"
            min="1"
            max="5"
            step="1"
            value={moodValue}
            onChange={handleSliderChange}
            onMouseDown={handleSliderInteraction}
            onTouchStart={handleSliderInteraction}
            onFocus={handleSliderInteraction}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className="mood-slider w-full h-3 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-4 focus:ring-gray-300 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#E5E5E5' }}
            aria-label="Mood rating from 1 to 5"
            aria-valuenow={moodValue}
            aria-valuetext={MOOD_LABELS[moodValue as keyof typeof MOOD_LABELS]}
          />
          
        </div>

        {/* Continue Button */}
        {hasInteracted && (
          <div className="flex justify-center pt-4">
            <Button
              onClick={handleContinue}
              disabled={disabled}
              className="px-8 py-3 font-semibold text-base transition-all duration-200 min-w-[200px] flex items-center justify-center text-white border-none rounded-2xl"
              style={{
                background: 'linear-gradient(to right, #6366f1, #a855f7)',
                color: '#FFFFFF',
                borderRadius: '16px',
                border: 'none',
                fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to right, #4f46e5, #9333ea)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to right, #6366f1, #a855f7)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              aria-label={`Continue with mood rating: ${MOOD_LABELS[moodValue as keyof typeof MOOD_LABELS]}`}
            >
              CONTINUE
            </Button>
          </div>
        )}
      </div>


      <style jsx>{`
        .mood-slider::-webkit-slider-thumb {
          appearance: none;
          height: 24px;
          width: 24px;
          border-radius: 50%;
          background: #111111;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }

        .mood-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .mood-slider::-webkit-slider-thumb:active {
          transform: scale(1.05);
        }

        .mood-slider::-moz-range-thumb {
          height: 24px;
          width: 24px;
          border-radius: 50%;
          background: #111111;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }

        .mood-slider::-moz-range-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .mood-slider::-moz-range-thumb:active {
          transform: scale(1.05);
        }

        .mood-slider::-ms-thumb {
          height: 24px;
          width: 24px;
          border-radius: 50%;
          background: #111111;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }

        .mood-slider::-ms-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .mood-slider::-ms-thumb:active {
          transform: scale(1.05);
        }

        .mood-slider::-webkit-slider-track {
          width: 100%;
          height: 12px;
          cursor: pointer;
          background: #E5E5E5;
          border-radius: 6px;
        }

        .mood-slider::-moz-range-track {
          width: 100%;
          height: 12px;
          cursor: pointer;
          background: #E5E5E5;
          border-radius: 6px;
          border: none;
        }

        .mood-slider::-ms-track {
          width: 100%;
          height: 12px;
          cursor: pointer;
          background: #E5E5E5;
          border-radius: 6px;
          border: none;
          color: transparent;
        }

        .mood-slider::-ms-fill-lower,
        .mood-slider::-ms-fill-upper {
          background: #E5E5E5;
          border-radius: 6px;
        }

        .mood-slider:focus {
          outline: none;
        }

        .mood-slider:disabled::-webkit-slider-thumb,
        .mood-slider:disabled::-moz-range-thumb,
        .mood-slider:disabled::-ms-thumb {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
      `}</style>
    </div>
  );
};

export default MoodSlider;